import { useEffect, useState, useCallback, useRef } from "react";
import {
    FaDatabase,
    FaRobot,
    FaServer,
    FaCheckCircle,
    FaTimesCircle,
    FaSyncAlt,
    FaClock,
    FaHistory,
    FaExclamationTriangle,
    FaMapMarkerAlt
} from "react-icons/fa";

import "./Dashboard.css";
import { StatusPill, BatteryBar } from "../components/RobotStatusUI";

// API_URL is the API base only, e.g. http://172.20.10.2:8000/api
// The actual health route is appended below as HEALTH_ENDPOINT.
const API_URL = process.env.REACT_APP_API_URL;
const API_HEALTH_ENDPOINT = process.env.REACT_APP_API_URL_HEALTH;

// NOTE: must be prefixed with REACT_APP_ or Create React App will not
// expose it to the browser bundle (process.env.SOAP_API_URL would
// always be undefined without the prefix).
const SOAP_API_URL = process.env.REACT_APP_SOAP_API_URL;

// Per the Swagger doc: GET /api/admin/health
const HEALTH_ENDPOINT = `${API_HEALTH_ENDPOINT}/admin/health`;

// Per the Swagger doc: GET /api/portal/last
const LAST_SYNC_ENDPOINT = `${API_URL}/portal/last`;

// Per the Swagger doc: GET /api/portal/robots/fleet/summary
const FLEET_SUMMARY_ENDPOINT = `${API_URL}/portal/robots/fleet/summary`;

// Per-robot live status (online, battery, current task, emergency
// stop state) - same endpoint RobotFleet.js and RobotDetail.js use.
const FLEET_ENDPOINT = `${API_URL}/portal/robots/fleet`;
const QUICK_POSITION_ENDPOINT = (robotSn) => `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/quick-position`;

// Dashboard-only fallback for the known production robots/maps.
// This is intentionally isolated to Map display and does not affect
// polling, commands, navigation, charging, or any backend behavior.
const KNOWN_ROBOT_MAP_NAMES = {
    MC1BHW2K10025021MAE5: "CialMK-0813174522",
    M03SHW2A26026010YB02: "Koothambalam-0914142818",
};
const KNOWN_MAP_POINTS = new Set([
    "Reception Point",
    "Charging Pole",
    "Charging Point",
]);

const showSoapCard = false;

const SOAP_REQUEST_BODY = `<?xml version="1.0" encoding="UTF-8"?>
<soap:Envelope
    xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
    xmlns:wsse="http://schemas.xmlsoap.org/ws/2002/12/secext">
    <soap:Header>
        <wsse:Security>
            <wsse:UsernameToken>
                <wsse:Username>tcs_user</wsse:Username>
                <wsse:Password>tcs_password</wsse:Password>
            </wsse:UsernameToken>
        </wsse:Security>
    </soap:Header>
    <soap:Body>
        <HealthCheck/>
    </soap:Body>
</soap:Envelope>`;

const AUTO_REFRESH_MS = 60000; // refresh full dashboard data every 60 seconds
const QUICK_POSITION_REFRESH_MS = 3000; // refresh only robot map-point position every 3 seconds

function StatusBadge({ ok }) {

    return (

        <span className={`status-badge ${ok ? "status-ok" : "status-down"}`}>

            {ok ? <FaCheckCircle /> : <FaTimesCircle />}

            {ok ? "Online" : "Down"}

        </span>

    );

}

// Shows two related sub-services (Database + LLM) in a single card,
// each with its own status badge/message, since they're two distinct
// health checks that happen to come from the same /admin/health call.
function CombinedServiceCard({ icon, title, loading, subItems, checkedAt, errorMessage }) {

    const allOk = !loading && subItems.every(item => item.ok);

    return (

        <div className={`service-card ${loading ? "is-loading" : allOk ? "card-ok" : "card-down"}`}>

            <div className="service-card-top">

                <div className="service-icon">
                    {icon}
                </div>

                {
                    loading
                        ? <span className="status-badge status-checking">
                            <FaSyncAlt className="spin" /> Checking
                        </span>
                        : <StatusBadge ok={allOk} />
                }

            </div>

            <h3>{title}</h3>

            <div className="service-subitem-list">

                {subItems.map((item) => (

                    <div className="service-subitem" key={item.label}>

                        <span className="service-subitem-label">{item.label}</span>

                        {
                            loading
                                ? <span className="status-badge status-checking">
                                    <FaSyncAlt className="spin" /> Checking
                                </span>
                                : <StatusBadge ok={item.ok} />
                        }

                        <span className="service-subitem-message">
                            {loading ? "" : (errorMessage || item.message || "No message")}
                        </span>

                    </div>

                ))}

            </div>

            {

                checkedAt &&

                <div className="service-extra">
                    <span className="extra-line">
                        Checked at: {formatDateTime(checkedAt)}
                    </span>
                </div>

            }

        </div>

    );

}

// Robot Fleet at-a-glance - total count plus a breakdown of
// online/offline/idle/low-battery/charging, from
// GET /portal/robots/fleet/summary.
function FleetSummaryCard({ loading, summary, errorMessage }) {

    const ok = !loading && !errorMessage && summary && summary.offline === 0;

    return (

        <div className={`service-card ${loading ? "is-loading" : ok ? "card-ok" : "card-down"}`}>

            <div className="service-card-top">

                <div className="service-icon">
                    <FaRobot />
                </div>

                {
                    loading
                        ? <span className="status-badge status-checking">
                            <FaSyncAlt className="spin" /> Checking
                        </span>
                        : <StatusBadge ok={ok} />
                }

            </div>

            <h3>Robot Fleet</h3>

            <p className="service-message">
                {

                    loading

                        ? "Checking fleet status..."

                        : errorMessage

                            ? errorMessage

                            : summary

                                ? `${summary.total_robots} robot${summary.total_robots === 1 ? "" : "s"} registered`

                                : "No data"

                }
            </p>

            {

                !loading && !errorMessage && summary &&

                <div className="fleet-count-grid">

                    <div className="fleet-count-item fleet-count-online">
                        <span className="fleet-count-value">{summary.online}</span>
                        <span className="fleet-count-label">Online</span>
                    </div>

                    <div className="fleet-count-item fleet-count-offline">
                        <span className="fleet-count-value">{summary.offline}</span>
                        <span className="fleet-count-label">Offline</span>
                    </div>

                    <div className="fleet-count-item">
                        <span className="fleet-count-value">{summary.idle}</span>
                        <span className="fleet-count-label">Idle</span>
                    </div>

                    <div className="fleet-count-item">
                        <span className="fleet-count-value">{summary.charging}</span>
                        <span className="fleet-count-label">Charging</span>
                    </div>

                    <div className="fleet-count-item fleet-count-low-battery">
                        <span className="fleet-count-value">{summary.low_battery}</span>
                        <span className="fleet-count-label">Low Battery</span>
                    </div>

                </div>

            }

        </div>

    );

}



function ServiceCard({ icon, title, ok, message, extra, loading }) {

    return (

        <div className={`service-card ${loading ? "is-loading" : ok ? "card-ok" : "card-down"}`}>

            <div className="service-card-top">

                <div className="service-icon">
                    {icon}
                </div>

                {

                    loading

                        ? <span className="status-badge status-checking">
                            <FaSyncAlt className="spin" /> Checking
                        </span>

                        : <StatusBadge ok={ok} />

                }

            </div>

            <h3>{title}</h3>

            <p className="service-message">
                {loading ? "Checking service status..." : message || "No message"}
            </p>

            {

                extra &&

                <div className="service-extra">
                    {extra}
                </div>

            }

        </div>

    );

}

function formatDateTime(value) {

    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

}

function Dashboard() {

    const [health, setHealth] = useState(null);       // REST /admin/health payload
    const [healthLoading, setHealthLoading] = useState(true);
    const [healthError, setHealthError] = useState(null);

    const [soap, setSoap] = useState(null);            // parsed SOAP payload
    const [soapLoading, setSoapLoading] = useState(true);
    const [soapError, setSoapError] = useState(null);

    const [lastSync, setLastSync] = useState(null);    // GET /portal/last payload
    const [lastSyncLoading, setLastSyncLoading] = useState(true);
    const [lastSyncError, setLastSyncError] = useState(null);

    const [fleetSummary, setFleetSummary] = useState(null);   // GET /portal/robots/fleet/summary payload
    const [fleetSummaryLoading, setFleetSummaryLoading] = useState(true);
    const [fleetSummaryError, setFleetSummaryError] = useState(null);

    const [fleet, setFleet] = useState([]);   // GET /portal/robots/fleet payload - per-robot status
    const [fleetLoading, setFleetLoading] = useState(true);
    const [fleetError, setFleetError] = useState(null);

    const [lastChecked, setLastChecked] = useState(null);

    const intervalRef = useRef(null);

    const checkRestHealth = useCallback(async () => {

        setHealthLoading(true);
        setHealthError(null);

        try {

            const res = await fetch(HEALTH_ENDPOINT, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setHealth(data);

        } catch (err) {

            console.error("Health check failed:", err);

            setHealthError(err.message || "Unable to reach health API");

            setHealth(null);

        } finally {

            setHealthLoading(false);

        }

    }, []);

    const checkSoapHealth = useCallback(async () => {

        setSoapLoading(true);
        setSoapError(null);

        try {

            const res = await fetch(SOAP_API_URL, {

                method: "POST",

                headers: {
                    "Content-Type": "text/xml; charset=utf-8"
                },

                body: SOAP_REQUEST_BODY

            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const xmlText = await res.text();

            const parser = new DOMParser();

            const xmlDoc = parser.parseFromString(xmlText, "text/xml");

            const parserErr = xmlDoc.querySelector("parsererror");

            if (parserErr) {
                throw new Error("Invalid XML response from SOAP service");
            }

            const getTag = (tag) => {
                const node = xmlDoc.getElementsByTagName(tag)[0];
                return node ? node.textContent : null;
            };

            const status = getTag("Status");

            setSoap({
                ok: status === "true" || status === "1",
                message: getTag("Message"),
                databaseStatus: getTag("DatabaseStatus"),
                dateTime: getTag("DateTime")
            });

        } catch (err) {

            console.error("SOAP health check failed:", err);

            setSoapError(err.message || "Unable to reach SOAP API");

            setSoap(null);

        } finally {

            setSoapLoading(false);

        }

    }, []);

    // =====================================================
    // Last Sync (GET /portal/last)
    // =====================================================
    const checkLastSync = useCallback(async () => {

        setLastSyncLoading(true);
        setLastSyncError(null);

        try {

            const res = await fetch(LAST_SYNC_ENDPOINT, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setLastSync(data);

        } catch (err) {

            console.error("Last sync check failed:", err);

            setLastSyncError(err.message || "Unable to reach last sync API");

            setLastSync(null);

        } finally {

            setLastSyncLoading(false);

        }

    }, []);

    // =====================================================
    // =====================================================
    // Robot Fleet Summary (GET /portal/robots/fleet/summary)
    // =====================================================
    const checkFleetSummary = useCallback(async () => {

        setFleetSummaryLoading(true);
        setFleetSummaryError(null);

        try {

            const res = await fetch(FLEET_SUMMARY_ENDPOINT, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setFleetSummary(data);

        } catch (err) {

            console.error("Fleet summary check failed:", err);

            setFleetSummaryError(err.message || "Unable to reach fleet summary API");

            setFleetSummary(null);

        } finally {

            setFleetSummaryLoading(false);

        }

    }, []);

    const checkFleet = useCallback(async () => {

        setFleetLoading(true);
        setFleetError(null);

        try {

            const res = await fetch(FLEET_ENDPOINT, {
                headers: {
                    accept: "application/json"
                }
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setFleet(Array.isArray(data) ? data : []);

        } catch (err) {

            console.error("Fleet list check failed:", err);

            setFleetError(err.message || "Unable to reach fleet list API");

            setFleet([]);

        } finally {

            setFleetLoading(false);

        }

    }, []);

    const refreshQuickPositions = useCallback(async () => {

        if (!fleet.length) {
            return;
        }

        const updates = await Promise.all(
            fleet.map(async (robot) => {
                try {
                    const res = await fetch(QUICK_POSITION_ENDPOINT(robot.robot_sn), {
                        headers: { accept: "application/json" },
                        cache: "no-store"
                    });

                    if (!res.ok) {
                        throw new Error(`Request failed with status ${res.status}`);
                    }

                    const data = await res.json();
                    return {
                        robotSn: robot.robot_sn,
                        data
                    };
                } catch (err) {
                    console.warn(`Quick position update failed for ${robot.robot_sn}:`, err);
                    return null;
                }
            })
        );

        const validUpdates = updates.filter(Boolean);
        if (!validUpdates.length) {
            return;
        }

        const byRobot = new Map(validUpdates.map(({ robotSn, data }) => [robotSn, data]));

        setFleet((current) => current.map((robot) => {
            const update = byRobot.get(robot.robot_sn);
            if (!update) {
                return robot;
            }

            return {
                ...robot,
                map_name: update.map_name || robot.map_name,
                pos_name: update.pos_name || robot.pos_name,
                location_state: update.location_state || robot.location_state,
                is_emergency_stop: typeof update.is_emergency_stop === "boolean"
                    ? update.is_emergency_stop
                    : robot.is_emergency_stop,
                last_polled_at: update.server_time
                    ? new Date(update.server_time).toISOString()
                    : robot.last_polled_at
            };
        }));

    }, [fleet]);

    const quickPositionRef = useRef(refreshQuickPositions);

    useEffect(() => {
        quickPositionRef.current = refreshQuickPositions;
    }, [refreshQuickPositions]);

    useEffect(() => {
        if (!fleet.length) {
            return undefined;
        }

        quickPositionRef.current();
        const timer = setInterval(() => quickPositionRef.current(), QUICK_POSITION_REFRESH_MS);

        return () => clearInterval(timer);
    }, [fleet.length]);

    const checkAll = useCallback(() => {

        setLastChecked(new Date());

        checkRestHealth();

        if (showSoapCard) {

            checkSoapHealth();

        }

        checkLastSync();

        checkFleetSummary();

        checkFleet();

    }, [checkRestHealth, checkSoapHealth, checkLastSync, checkFleetSummary, checkFleet]);

    // Always call the latest checkAll (it closes over fromDate/toDate),
    // without needing to restart the interval every time those change.
    const checkAllRef = useRef(checkAll);

    useEffect(() => {

        checkAllRef.current = checkAll;

    }, [checkAll]);

    useEffect(() => {

        checkAllRef.current();

        intervalRef.current = setInterval(() => checkAllRef.current(), AUTO_REFRESH_MS);

        return () => clearInterval(intervalRef.current);

    }, []); // set up once on mount; checkAllRef always has the latest closure

    const anyLoading = healthLoading || fleetSummaryLoading || (showSoapCard && soapLoading);

    const allOk =
        !healthLoading &&
        (!showSoapCard || !soapLoading) &&
        health?.all_ok &&
        lastSync?.status &&
        !fleetSummaryLoading &&
        !fleetSummaryError &&
        fleetSummary &&
        fleetSummary.offline === 0;

    return (

        <div className="dashboard">

            <div className="dashboard-header">

                <div>
                    <h2>Dashboard</h2>
                    <p className="dashboard-subtitle">
                        Welcome to Dashboard -- live service health overview
                    </p>
                </div>

                <div className="dashboard-actions">

                    {

                        lastChecked &&

                        <span className="last-checked">
                            <FaClock />
                            Last checked: {lastChecked.toLocaleTimeString()}
                        </span>

                    }

                    <button
                        type="button"
                        className="refresh-btn"
                        onClick={checkAll}
                        disabled={anyLoading}
                    >

                        <FaSyncAlt className={anyLoading ? "spin" : ""} />

                        {anyLoading ? "Checking..." : "Refresh"}

                    </button>

                </div>

            </div>

            <div className={`overall-banner ${anyLoading ? "banner-checking" : allOk ? "banner-ok" : "banner-down"}`}>

                {

                    anyLoading

                        ? <><FaSyncAlt className="spin" /> Checking all services...</>

                        : allOk

                            ? <><FaCheckCircle /> All systems operational</>

                            : <><FaTimesCircle /> One or more services are experiencing issues</>

                }

            </div>

            <div className="service-grid">

                <CombinedServiceCard
                    icon={<FaDatabase />}
                    title="LLM Service & Database"
                    loading={healthLoading}
                    errorMessage={healthError}
                    checkedAt={health?.checked_at}
                    subItems={[
                        {
                            label: "Database",
                            ok: health?.services?.database?.ok,
                            message: health?.services?.database?.message
                        },
                        {
                            label: "LLM Service",
                            ok: health?.services?.llm?.ok,
                            message: health?.services?.llm?.message
                        }
                    ]}
                />

                <FleetSummaryCard
                    loading={fleetSummaryLoading}
                    summary={fleetSummary}
                    errorMessage={fleetSummaryError}
                />
                {showSoapCard && (
                <ServiceCard                
                    icon={<FaServer />}
                    title="SOAP API"
                    loading={soapLoading}
                    ok={soap?.ok}                
                    message={
                        soapError
                            ? soapError
                            : soap?.message
                    }
                    extra={

                        soap &&

                        <>
                            <span className="extra-line">
                                Database: {soap.databaseStatus}
                            </span>

                            <span className="extra-line">
                                Checked at: {formatDateTime(soap.dateTime)}
                            </span>
                        </>

                    }
                /> 
                )}
                <ServiceCard
                    icon={<FaHistory />}
                    title="AODB Last Sync"
                    loading={lastSyncLoading}
                    ok={lastSync?.status}
                    message={
                        lastSyncError
                            ? lastSyncError
                            : lastSync?.remarks
                    }
                    extra={

                        lastSync &&

                        <>
                            <span className="extra-line">
                                API: {lastSync.api_name}
                            </span>

                            <span className="extra-line">
                                Synced at: {formatDateTime(lastSync.created_at)}
                            </span>
                        </>

                    }
                />

            </div>


            <div className="log-section">

                <div className="log-section-header">
                    <div>
                        <h3><FaRobot /> Robot Status</h3>
                        <p className="log-section-note">Live status for every robot in the fleet</p>
                    </div>
                </div>

                {fleetLoading && (
                    <p className="empty-note"><FaSyncAlt className="spin" /> Loading robot status...</p>
                )}

                {!fleetLoading && fleetError && (
                    <p className="empty-note">{fleetError}</p>
                )}

                {!fleetLoading && !fleetError && fleet.length === 0 && (
                    <p className="empty-note">No robots registered yet.</p>
                )}

                {!fleetLoading && !fleetError && fleet.length > 0 && (

                    <div className="robot-status-list">

                        {fleet.map((robot) => (

                            <div className="robot-status-row" key={robot.robot_sn}>

                                <div className="robot-status-name">
                                    <span className="robot-status-title">{robot.robot_name || robot.robot_sn}</span>
                                    <span className="robot-status-sn">{robot.robot_sn}</span>
                                </div>

                                <StatusPill robot={robot} />

                                <BatteryBar pct={robot.battery_rate} />

                            </div>

                        ))}

                    </div>

                )}

            </div>

            <div className="log-section">

                <div className="log-section-header">
                    <div>
                        <h3><FaMapMarkerAlt /> Live Robot Position</h3>
                        <p className="log-section-note">
                            Current named map position reported by each robot
                        </p>
                    </div>
                </div>

                {fleetLoading && (
                    <p className="empty-note"><FaSyncAlt className="spin" /> Loading live positions...</p>
                )}

                {!fleetLoading && fleetError && (
                    <p className="empty-note">{fleetError}</p>
                )}

                {!fleetLoading && !fleetError && fleet.length === 0 && (
                    <p className="empty-note">No robots registered yet.</p>
                )}

                {!fleetLoading && !fleetError && fleet.length > 0 && (
                    <div className="robot-position-grid">
                        {fleet.map((robot) => {
                            const hasPosition = Boolean(robot.pos_name);
                            const fallbackMapName = KNOWN_ROBOT_MAP_NAMES[robot.robot_sn];
                            const displayMapName = robot.map_name || (KNOWN_MAP_POINTS.has(robot.pos_name) ? fallbackMapName : null);
                            const hasMap = Boolean(displayMapName);
                            const positioningReady = robot.location_state === "ready";
                            const positionLabel = hasPosition
                                ? robot.pos_name
                                : positioningReady
                                    ? "Position unavailable"
                                    : "Positioning unavailable";

                            return (
                                <div className="robot-position-card" key={robot.robot_sn}>
                                    <div className="robot-position-card-header">
                                        <div className="robot-status-name">
                                            <span className="robot-status-title">
                                                {robot.robot_name || robot.robot_sn}
                                            </span>
                                            <span className="robot-status-sn">{robot.robot_sn}</span>
                                        </div>

                                        <span
                                            className={`robot-position-state ${
                                                positioningReady && hasPosition
                                                    ? "position-ready"
                                                    : "position-unavailable"
                                            }`}
                                        >
                                            <FaMapMarkerAlt />
                                            {positioningReady && hasPosition && hasMap ? "Located" : "Unavailable"}
                                        </span>
                                    </div>

                                    <div className="robot-position-main">
                                        <FaMapMarkerAlt className="robot-position-pin" />
                                        <div>
                                            <span className="robot-position-label">Map</span>
                                            <strong>{displayMapName || "Map -"}</strong>
                                            <span className="robot-position-point-label">Map Point</span>
                                            <strong>{positionLabel}</strong>
                                        </div>
                                    </div>

                                    <div className="robot-position-meta">
                                        <span>
                                            Status:{" "}
                                            {robot.location_state === "ready"
                                                ? "Positioning ready"
                                                : robot.location_state === "get_lost"
                                                    ? "Robot has lost positioning"
                                                    : "Unknown"}
                                        </span>
                                        <span>
                                            Updated: {formatDateTime(robot.last_polled_at)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

            </div>

            <div className="log-section">

                <div className="log-section-header">
                    <div>
                        <h3><FaExclamationTriangle /> Emergency Alerts</h3>
                        <p className="log-section-note">
                            Robots currently in emergency-stop state - a live check, not a history of past events
                        </p>
                    </div>
                </div>

                {fleetLoading && (
                    <p className="empty-note"><FaSyncAlt className="spin" /> Checking for active alerts...</p>
                )}

                {!fleetLoading && fleetError && (
                    <p className="empty-note">{fleetError}</p>
                )}

                {!fleetLoading && !fleetError && (

                    (() => {

                        const emergencyRobots = fleet.filter(r => r.is_emergency_stop);

                        if (emergencyRobots.length === 0) {

                            return <p className="empty-note ok-note"><FaCheckCircle /> No robots currently in emergency-stop state.</p>;

                        }

                        return (

                            <div className="robot-status-list">

                                {emergencyRobots.map((robot) => (

                                    <div className="robot-status-row emergency-row" key={robot.robot_sn}>

                                        <div className="robot-status-name">
                                            <span className="robot-status-title">{robot.robot_name || robot.robot_sn}</span>
                                            <span className="robot-status-sn">{robot.robot_sn}</span>
                                        </div>

                                        <StatusPill robot={robot} />

                                    </div>

                                ))}

                            </div>

                        );

                    })()

                )}

            </div>


        </div>

    );

}

export default Dashboard;